<template>
	<v-combobox ref="comboboxRef" v-model="tokens" :label="props.label" multiple chips :hint="props.hint" persistent-hint :rules="validationRules" :density="props.density" variant="outlined" hide-details="auto" @update:search="pendingToken = $event" @paste="onPaste">
		<template v-if="props.icon" #prepend-inner>
			<v-icon>{{ props.icon }}</v-icon>
		</template>
		<template v-slot:selection="{ item, index }">
			<v-chip v-if="index < 2" closable @click:close="removeToken(index)">{{ item.title }}</v-chip>
			<span v-if="index === 2">(+{{ tokens.length - 2 }})</span>
		</template>
	</v-combobox>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useTranslator } from '@/composables/useTranslator';

const props = defineProps({
	label: { type: String, default: '' },
	hint: { type: String, default: '' },
	// Semantic icon (label / qr-code / tag) — this field is free-text "contains", never a search picker.
	icon: { type: String, default: null },
	required: { type: Boolean, default: false },
	density: { type: String, default: 'compact' },
	modelValue: { type: Array, default: undefined },
	preSelected: { type: Array, default: null },
});
const emit = defineEmits(['update:modelValue', 'change']);

const t = useTranslator();
const comboboxRef = ref(null);
const tokens = ref(Array.isArray(props.preSelected) ? [...props.preSelected] : []);
const pendingToken = ref('');
const effectiveTokens = computed(() => {
	const pending = normalizeToken(pendingToken.value);
	return pending && !tokens.value.includes(pending) ? [...tokens.value, pending] : [...tokens.value];
});

const validationRules = computed(() => {
	if (!props.required) return [];
	return [() => effectiveTokens.value.length > 0 || t('FieldRequired')];
});

watch(
	() => props.preSelected,
	(val) => {
		if (props.modelValue !== undefined) return;
		tokens.value = Array.isArray(val) ? [...val] : [];
	}
);

watch(
	() => props.modelValue,
	(val) => {
		if (val === undefined) return;
		tokens.value = Array.isArray(val) ? [...val] : [];
	},
	{ immediate: true }
);

watch(tokens, (val) => emit('update:modelValue', [...val]), { deep: true });
watch(effectiveTokens, (val) => emit('change', val));

function normalizeToken(rawToken) {
	return String(rawToken ?? '').trim();
}

function addTokens(rawTokens) {
	const additions = rawTokens.map(normalizeToken).filter(Boolean);
	if (!additions.length) return;
	const existing = new Set(tokens.value);
	additions.forEach((token) => existing.add(token));
	tokens.value = [...existing];
}

// No items/menu here — this is free-text only, so paste can't rely on v-combobox's own single-entry
// buffer. Always split on the usual list separators: a plain paste (no separator) still yields one token.
function onPaste(event) {
	const pastedText = event.clipboardData?.getData('text');
	if (!pastedText) return;
	event.preventDefault();
	addTokens(pastedText.split(/[,;\t\n]+/));
}

function removeToken(index) {
	tokens.value = tokens.value.filter((_, candidateIndex) => candidateIndex !== index);
}

function clearPendingToken() {
	pendingToken.value = '';
	if (comboboxRef.value) {
		comboboxRef.value.search = '';
	}
}

defineExpose({
	getValues() {
		return effectiveTokens.value.length > 0 ? [...effectiveTokens.value] : null;
	},
	getFullValues() {
		return effectiveTokens.value.length > 0 ? [...effectiveTokens.value] : null;
	},
	isDirty() {
		return effectiveTokens.value.length > 0;
	},
	isValid() {
		if (!props.required) return true;
		return effectiveTokens.value.length > 0;
	},
	isRequired() {
		return props.required;
	},
	selectItems(items) {
		tokens.value = Array.isArray(items) ? [...items] : [];
		clearPendingToken();
	},
	reset() {
		tokens.value = [];
		clearPendingToken();
	},
});
</script>
